<template>
  <ElAutocomplete
    v-model="searchInput"
    :fetch-suggestions="guestSearch"
    :debounce="400"
    :trigger-on-focus="true"
    resize="both"
    clearable
    placeholder="Search for guests"
    popper-class="search-results-container"
    inputmode="search"
    class="search-input"
    @select="onSearchResultSelect"
  >
    <!--@keyup.enter="onSearchInput"-->
    <!--@input="onSearchInput"-->
    <div slot="prefix">
      <FontAwesomeIcon
        :icon="['far', 'search']"
        class="h-40 text-primary-light ml-10"
      />
    </div>

    <template
      v-if="!guestIsInitialLoading"
      v-slot="{ item }"
    >
      <GuestListItem
        v-if="item.type !== 'message'"
        :item="item.guest"
        :item-index="item.index"
        :item-height="55"
        :avatar-chip-size="40"
        hide-edit
        hide-delete
        @guest-update="$emit('guest-update', $event)"
      />
      <p
        v-else
        class="flex items-center text-base text-primary-light py-10 px-0 mx-20"
      >
        <FontAwesomeIcon
          v-if="item.icon"
          :icon="['fas', 'info-circle']"
          class="text-2xl mr-15"
        />
        <span class="whitespace-normal">
          {{ item.text }}
        </span>
      </p>
    </template>
  </ElAutocomplete>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Fuse from 'fuse.js';
import GuestListItem from './list/GuestListItem';

export default {
  props: {
    firstNameFirstOrder: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    GuestListItem,
  },
  data: () => ({
    searchInput: '',
    guestCountLimit: 40,
  }),
  computed: {
    ...mapState('guest', {
      guestIsInitialLoading: 'isInitialLoading',
    }),
    ...mapGetters('guest/filter', {
      filteredGuests: 'filteredGuests',
    }),
  },
  methods: {
    ...mapActions('guest', {
      updateGuestPhotoURL: 'updateGuestPhotoURL',
    }),
    onSearchResultSelect(item) {
      if (item.type === 'message') return;

      this.searchInput = '';
    },
    guestSearch(queryString, callback) {
      let messageText = '';

      if (queryString !== undefined && queryString.length < 3) {
        if (queryString.length === 0) {
          messageText = 'Type the first/last name, email, phone number, company name or notes';
        } else {
          messageText = `Enter ${3 - queryString.length} more character${(3 - queryString.length) === 1 ? '' : 's'}`;
        }

        callback([{
          type: 'message',
          text: messageText,
          icon: queryString.length === 0,
        }]);
        return;
      }

      const searchResults = this.searchInGuest(queryString);
      const limitedSearchResults = searchResults.slice(0, this.guestCountLimit);
      const results = [];

      limitedSearchResults.forEach((searchResult) => {
        results.push({
          value: searchResult.item.firstName,
          label: searchResult.item.id,
          index: results.length - 1,
          guest: searchResult.item,
        });
        // Bug: The guest data here is not reactive (Limitation of Element UI's component)

        // Update guest photoURLs of filtered results if not previously set
        if (searchResult.item.photoStoragePath && !searchResult.item.photoURL) {
          this.updateGuestPhotoURL(searchResult.item.listIndex);
        }
      });

      if (searchResults.length > this.guestCountLimit) {
        results.push({
          type: 'message',
          text: `+ ${searchResults.length - this.guestCountLimit} more`,
        });
      }

      callback(results);
    },
    searchInGuest(queryString) { // TODO: Normalize search input
      const options = {
        shouldSort: true,
        includeMatches: true,
        threshold: 0.3,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: [{
          name: 'firstName',
          weight: this.firstNameFirstOrder ? 0.9 : 0.8,
        }, {
          name: 'lastName',
          weight: this.firstNameFirstOrder ? 0.8 : 0.9,
        }, {
          name: 'email',
          weight: 0.6,
        }, {
          name: 'phone',
          weight: 0.4,
        }, {
          name: 'company',
          weight: 0.4,
        }, {
          name: 'notes',
          weight: 0.4,
        }],
      };
      const fuse = new Fuse(this.filteredGuests.guests, options);
      return fuse.search(queryString);
    },
  },
};
</script>

<style>
/* Autocomplete input */
.search-input {
  .el-input__inner {
    @apply pl-50 bg-gray-300 rounded !important;

    &::placeholder {
      @apply text-primary tracking-wide opacity-90;
    }

    &:focus {
      @apply border-primary-light;
    }
  }

  .el-input__prefix {
    @apply text-lg text-primary-light;
  }
}

/* El-Autocomplete */
.search-results-container.el-autocomplete-suggestion {
  @apply scrolling-touch;

  .el-autocomplete-suggestion__wrap {
    max-height: 315px;

    li[role="option"] {
      @apply leading-tight p-0;

      &:hover {
        @apply bg-gray-100;
      }
    }
  }
}
</style>
